import { navigateTo, useRouter, useRuntimeConfig } from '#imports'

import { scopedStorage } from '../utils/storage'

import { useRedirectLocale } from './useRedirectLocale'
import { useUserStore } from './useUserStore'

type StoredParams = {
  next: string | null
}

export const NEXT_KEY = 'redirection'
const USER_ID_PLACEHOLDER = 'USER_ID'

/**
 * This custom error represents the case where an absolute URL is passed as
 * next query parameter, but is not allowed based on the module configuration.
 *
 * @see `allowedExternalDomains` module configuration property.
 */
export class NextLocationNotAllowedError extends Error {}

/**
 * Redirect the user to the next page. The next page location is computed from
 * the next query parameter, or fallbacks to the default route location defined
 * using `defaultRedirectLocationWhenAuthenticated` in the module configuration.
 *
 * @throws {NextLocationNotAllowedError} when the next location is not allowed.
 */
export function useRedirect() {
  const router = useRouter()
  const config = useRuntimeConfig().public.oauth
  const { getRouteLocationWithLocale } = useRedirectLocale()
  const { user } = useUserStore()

  /**
   * Resolves a route location (relative or full URL) and determines if it's internal to the application
   * Route resolution only supports relative paths
   */
  const resolveRouteLocation = (location: string) => {
    try {
      const resolved = router.resolve(location)

      return {
        resolved,
        isInternal: resolved.matched.length > 0,
      }
    } catch {
      return {
        resolved: null,
        isInternal: false,
      }
    }
  }

  /**
   * Redirects to a specific location, handling both internal and external routes
   */
  const redirectToLocation = async (location: string) => {
    const { isInternal } = resolveRouteLocation(location)

    await navigateTo(location, {
      external: !isInternal,
      replace: true,
    })
  }

  /**
   * Gets the default redirect location when no stored next parameter exists
   */
  const getDefaultRedirectLocation = () => {
    return router.resolve(
      getRouteLocationWithLocale(
        config.defaultRedirectLocationWhenAuthenticated,
      ),
    ).fullPath
  }

  /**
   * Replaces USER_ID placeholder with actual user ID for SheerID onboarding
   */
  const replaceUserIdPlaceholder = (nextUrl: string) => {
    return user.value.bmUserId
      ? nextUrl.replace(USER_ID_PLACEHOLDER, user.value.bmUserId)
      : nextUrl
  }

  const isAllowedRedirectUrl = (url: URL) => {
    const isSameOrigin = url.origin === window.location.origin
    const isAllowedExternalDomain =
      !isSameOrigin && config.allowedExternalDomains.includes(url.origin)

    return isSameOrigin || isAllowedExternalDomain
  }

  /**
   * Processes the next location parameter and handles redirection logic
   */
  const processNextLocation = (nextLocation: string) => {
    // Replace USER_ID placeholder with actual user ID for SheerID onboarding
    const updatedNextLocation = replaceUserIdPlaceholder(nextLocation)

    // Create URL object to validate origin
    // The second parameter is only used when the first is a relative path
    const redirectUrl = new URL(updatedNextLocation, window.location.origin)

    // Validate external domain is allowed
    if (!isAllowedRedirectUrl(redirectUrl)) {
      throw new NextLocationNotAllowedError(
        `${redirectUrl.origin} is not allowed`,
      )
    }

    return redirectToLocation(updatedNextLocation)
  }

  const redirectAfterAuth = async () => {
    // Read from storage at redirect time to ensure we get the latest value
    const storedParams = scopedStorage.getItem(NEXT_KEY)

    if (storedParams === null) {
      return redirectToLocation(getDefaultRedirectLocation())
    }

    const { next } = JSON.parse(storedParams) as StoredParams

    // Clear the stored redirect after reading it to prevent stale redirects
    scopedStorage.removeItem(NEXT_KEY)

    if (next === null) {
      return redirectToLocation(getDefaultRedirectLocation())
    }

    // Process the next location with validation and redirection
    return processNextLocation(next)
  }

  return { redirectAfterAuth }
}
